import axios from 'axios';
import isArray from 'lodash/isArray';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getAPIUrl, getLabelClassName, getUrlPrefix } from './utils';
import { HumanDate } from '../widgets';
function DefinitionRow({ children }) {
    return (React.createElement("div", { className: "definition-row row py-2 border-bottom" }, children));
}
function DefinitionHeader({ children }) {
    return (React.createElement("div", { className: "col-sm-4" }, children));
}
function DefinitionDetails({ children }) {
    return (React.createElement("div", { className: "col-sm-8" }, children));
}
function DownloadIcon() {
    return (React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", fill: "currentColor", className: "bi bi-file-earmark-arrow-down", viewBox: "0 0 16 16" },
        React.createElement("path", { d: "M8.5 6.5a.5.5 0 0 0-1 0v3.793L6.354 9.146a.5.5 0 1 0-.708.708l2 2a.5.5 0 0 0 .708 0l2-2a.5.5 0 0 0-.708-.708L8.5 10.293V6.5z" }),
        React.createElement("path", { d: "M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z" })));
}
function AuditKitError({ message }) {
    const [isOpen, setOpen] = useState(true);
    useEffect(() => {
        setOpen(true);
    }, [message]);
    function dismiss() {
        setOpen(false);
    }
    if (!message)
        return null;
    if (!isOpen)
        return null;
    return (React.createElement("div", { className: "alert alert-warning alert-dismissible fade show", role: "alert" },
        React.createElement("strong", { className: "alert-heading" }, "Failed to download Audit Kit!"),
        React.createElement("p", null, message),
        React.createElement("p", null,
            "Please try again, and if you continue to have problems \u00A0",
            React.createElement("a", { href: "https://support.oetc.org/hc/en-us/requests/new", target: "_blank", rel: "noreferrer", className: "alert-link" }, "contact support"),
            "."),
        React.createElement("button", { type: "button", className: "btn-close", "data-bs-dismiss": "alert", "aria-label": "Close", onClick: dismiss })));
}
function AuditKitDownloadControl({ numberSlug, setError }) {
    const buttonClass = 'btn btn-primary btn-lg';
    const [isLoading, setLoadingFlag] = useState(false);
    const [downloadLink, setDownloadLink] = useState('');
    const [isDownloading, setDownloading] = useState(false);
    useEffect(() => {
        let pollTimeout = null;
        let token = null;
        function cleanup() {
            if (!pollTimeout)
                return;
            clearTimeout(pollTimeout);
            pollTimeout = null;
        }
        function poll() {
            setLoadingFlag(true);
            setError(null);
            console.log(token);
            let url = `${getAPIUrl()}${numberSlug}/audit-kit/`;
            if (token)
                url = `${url}${token}`;
            return axios.get(url).then(resp => {
                console.log(resp.data);
                if (resp.data?.link) {
                    setLoadingFlag(false);
                    setDownloadLink(resp.data?.link);
                    cleanup();
                    window.location.href = resp.data?.link;
                    return;
                }
                if (resp.data?.state === 'error') {
                    setLoadingFlag(false);
                    setDownloading(false);
                    setDownloadLink(null);
                    cleanup();
                    setError(resp.data?.message);
                    return;
                }
                if (!token && resp.data.token) {
                    token = resp.data.token;
                }
                pollTimeout = setTimeout(poll, 500);
            });
        }
        if (isDownloading === true) {
            poll();
        }
        else {
            cleanup();
        }
        window.addEventListener('beforeunload', cleanup);
        return () => {
            cleanup();
            window.removeEventListener('beforeunload', cleanup);
        };
    }, [isDownloading]);
    function startGeneration() {
        setDownloading(true);
    }
    if (!numberSlug)
        return null;
    if (isLoading) {
        return (React.createElement("button", { className: buttonClass, type: "button", disabled: true },
            React.createElement("span", { className: "spinner-border spinner-border-sm", role: "status", "aria-hidden": "true" }),
            "\u00A0 Generating Audit Kit..."));
    }
    if (downloadLink) {
        return (React.createElement("a", { href: downloadLink, download: true, className: buttonClass, rel: "noreferrer" },
            React.createElement(DownloadIcon, null),
            " Download Audit Kit (.ZIP)"));
    }
    return (React.createElement("button", { className: buttonClass, type: "button", onClick: startGeneration },
        React.createElement(DownloadIcon, null),
        " Download Audit Kit (.ZIP)"));
}
function AuditKitControl({ numberSlug }) {
    const [errorMessage, setErrorMessage] = useState(null);
    return (React.createElement("div", { className: "alert alert-info my-3", role: "alert" },
        React.createElement("h4", { className: "pt-1" }, "Audit Kit Available"),
        React.createElement(AuditKitError, { message: errorMessage }),
        React.createElement("div", { className: "row justify-content-sm-between align-items-sm-center" },
            React.createElement("div", { className: "col-sm-6 col-md-8" },
                React.createElement("p", null, "Need to provide documentation for an audit? Download an Audit Kit for this contract to access OETC's score sheets, vendor responses, and other contract details.")),
            React.createElement("div", { className: "col-sm-auto" },
                React.createElement(AuditKitDownloadControl, { numberSlug: numberSlug, setError: setErrorMessage })))));
}
function CommaSeparatedList({ title, entries, forceSort }) {
    if (!entries?.length)
        return null;
    function renderEntries() {
        const result = [...entries];
        if (forceSort)
            result.sort();
        return result.map((el, i) => (React.createElement("li", { className: "list-inline-item", key: i }, el)));
    }
    return (React.createElement(DefinitionRow, null,
        React.createElement(DefinitionHeader, null, title),
        React.createElement(DefinitionDetails, null,
            React.createElement("ul", { className: "list-inline mb-0 comma-separated" }, renderEntries()))));
}
function AwardDetailsView(award) {
    return (React.createElement(React.Fragment, null,
        React.createElement("section", { id: `award-${(award.id)}` },
            React.createElement("h4", null, award.holderName || 'Without Holder'),
            React.createElement("div", { className: "details-holder-stripped mb-3" },
                React.createElement(DefinitionRow, null,
                    React.createElement(DefinitionHeader, null, "Award Holder"),
                    React.createElement(DefinitionDetails, null, award.holderName)),
                (award.startedAt || award.expiresAt) && (React.createElement(DefinitionRow, null,
                    React.createElement(DefinitionHeader, null, "Award Duration"),
                    React.createElement(DefinitionDetails, null,
                        React.createElement(HumanDate, { usrDate: award.startedAt }),
                        "\u00A0 \u2014 \u00A0",
                        React.createElement(HumanDate, { usrDate: award.expiresAt })))),
                React.createElement(CommaSeparatedList, { entries: award.manufacturers, title: "Manufacturers Covered", forceSort: true }),
                award.resellersOverrideEnabled ? (React.createElement(DefinitionRow, null,
                    React.createElement(DefinitionHeader, null, "Named Resellers"),
                    React.createElement(DefinitionDetails, null, award.resellersOverride || ''))) : (React.createElement(CommaSeparatedList, { entries: award.resellers, title: "Named Resellers", forceSort: true }))),
            award.documents?.length > 0 && (React.createElement(React.Fragment, null,
                React.createElement("h5", null, "Award Documents"),
                React.createElement("ul", { className: "list-group list-group-flush" }, award.documents.map(document => (React.createElement("li", { key: document.id, className: "list-group-item" },
                    React.createElement("a", { rel: "noreferrer", target: "_blank", href: document.s3Link },
                        React.createElement(DownloadIcon, null),
                        " ",
                        document.title)))))))),
        React.createElement("hr", null)));
}
export function DetailsUI() {
    const navigate = useNavigate();
    const { numberSlug } = useParams();
    const [procurement, setProcurement] = useState();
    const [awards, setAwards] = useState([]);
    useEffect(() => {
        function handleNotFound() {
            navigate(`${getUrlPrefix()}?search_str=${encodeURIComponent(numberSlug)}`);
        }
        axios.get(`${getAPIUrl()}${numberSlug}`).then((resp) => {
            if (!resp.data?.procurement) {
                return handleNotFound();
            }
            setProcurement(resp.data?.procurement);
            setAwards(resp.data?.awards || []);
        }).catch(error => {
            if (error.response?.status === 404) {
                return handleNotFound();
            }
            if (error.response?.status === 301 && error.response?.data?.new_slug) {
                return navigate(`${getUrlPrefix()}d/${error.response.data.new_slug}`);
            }
            console.log(error);
        });
    }, [numberSlug]);
    function getCategories() {
        let result = [procurement.primaryCategory];
        if (isArray(procurement?.subCategories)) {
            result = [procurement.primaryCategory, ...(procurement.subCategories).sort()];
        }
        return result.filter(el => el);
    }
    if (!procurement)
        return null;
    return (React.createElement("div", { id: "procurement-details", className: "container" },
        React.createElement("header", null,
            React.createElement("nav", { "aria-label": "breadcrumb" },
                React.createElement("ol", { className: "breadcrumb" },
                    React.createElement("li", { className: "breadcrumb-item" },
                        React.createElement("a", { href: "/" }, "Home")),
                    React.createElement("li", { className: "breadcrumb-item" },
                        React.createElement(Link, { to: getUrlPrefix() }, "Procurements")),
                    React.createElement("li", { className: "breadcrumb-item active" }, procurement.name))),
            React.createElement("div", { className: "pb-2" },
                React.createElement("h2", { className: "mt-xxs" },
                    procurement.name,
                    "\u00A0",
                    React.createElement("span", { className: getLabelClassName(procurement.type) }, procurement.type)),
                React.createElement("hr", null),
                React.createElement("p", { className: "lead" },
                    React.createElement("strong", null, "Procurement Number: "),
                    " ",
                    procurement.number),
                React.createElement("hr", { className: "d-block d-md-none" }))),
        React.createElement(AuditKitControl, { numberSlug: procurement.numberSlug }),
        React.createElement("hr", null),
        React.createElement("section", { id: "documentation-procurement-details" },
            React.createElement("div", { className: "row mb-3" },
                React.createElement("div", { className: "col-md-3" },
                    React.createElement("div", { className: "sticky-top" },
                        React.createElement("h4", null, "Procurement Details"),
                        React.createElement("p", { className: "text-muted small" },
                            "For any questions about this procurement or its awards, please \u00A0",
                            React.createElement("a", { href: "/support", target: "_blank", className: "alert-link" }, "contact OETC"),
                            ".")),
                    React.createElement("hr", { className: "d-block d-md-none" })),
                React.createElement("div", { className: "col-md-9" },
                    React.createElement("div", { className: "details-holder-stripped mb-3" },
                        React.createElement(DefinitionRow, null,
                            React.createElement(DefinitionHeader, null, "Procurement Type"),
                            React.createElement(DefinitionDetails, null,
                                React.createElement("span", { className: getLabelClassName(procurement.type) }, procurement.type))),
                        procurement?.description && (React.createElement(DefinitionRow, null,
                            React.createElement(DefinitionHeader, null, "Description"),
                            React.createElement(DefinitionDetails, null, procurement.description || ''))),
                        procurement.announcementUrl && (React.createElement(DefinitionRow, null,
                            React.createElement(DefinitionHeader, null, "Procurement Announcement URL"),
                            React.createElement(DefinitionDetails, null,
                                React.createElement("a", { rel: "noreferrer", target: "_blank", href: procurement.announcementUrl },
                                    React.createElement("i", { className: "fa fa-external-link-square" }),
                                    " Announcement Url")))),
                        React.createElement(CommaSeparatedList, { entries: getCategories(), title: "Categories" }),
                        React.createElement(CommaSeparatedList, { entries: procurement?.states, title: "Advertised States", forceSort: true })),
                    procurement.documents?.length > 0 && (React.createElement(React.Fragment, null,
                        React.createElement("h4", null, "Procurement Documents"),
                        React.createElement("ul", { className: "list-group list-group-flush" }, procurement.documents.map(document => (React.createElement("li", { key: document.id, className: "list-group-item" },
                            React.createElement("a", { rel: "noreferrer", target: "_blank", href: document.s3Link },
                                React.createElement(DownloadIcon, null),
                                " ",
                                document.title)))))))))),
        awards?.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement("hr", null),
            React.createElement("section", { id: "documentation-awards" },
                React.createElement("div", { className: "row" },
                    React.createElement("div", { className: "col-md-3" },
                        React.createElement("h4", { className: "sticky-top" }, "Procurement Awards"),
                        React.createElement("hr", { className: "d-block d-md-none" })),
                    React.createElement("div", { className: "col-md-9" }, awards.map(award => (React.createElement(AwardDetailsView, { key: award.id, ...award }))))))))));
}
